// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {ButtonType} from 'common/components/button/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {createBannerShownEventFor, getTypePropOf} from './helpers';
import {
  ActionEvent,
  BannerAction,
  BannerAttrs,
  BannerJsxProps,
  BannerSize,
  BannerTheme,
  BannerType,
  CallToAction,
} from './types';
import {Template as template} from './template';
import css from './index.inline.styl';

const TRACK_BANNER_CTA_CLICK = `[mp-banner] Banner CTA Clicked`;

const BUTTON_TYPE_FROM_THEME = new Map<BannerTheme, ButtonType>([
  [BannerTheme.Error, ButtonType.Destructive],
  [BannerTheme.Info, ButtonType.Info],
  [BannerTheme.Success, ButtonType.Success],
  [BannerTheme.Warn, ButtonType.Warn],
  [BannerTheme.Marketing, ButtonType.Marketing],
  [BannerTheme.GradientBlue, ButtonType.Info],
  [BannerTheme.GradientRed, ButtonType.Info],
]);

export class CtaBanner extends Component<EmptyObj, BannerAttrs> {
  static get attrsSchema(): AttrsSchema<BannerAttrs> {
    return {
      ...super.attrsSchema,
      'banner-tracking-type': `string`,
      'body-text': `string`,
      ctas: `json`,
      dropdown: `json`,
      'footer-text': `string`,
      'header-text': `string`,
      icon: {type: `string`, default: `from-theme`},
      'img-src': `string`,
      multiline: `boolean`,
      'no-wrap-content': `boolean`,
      size: {type: `string`, enum: Object.values(BannerSize), default: BannerSize.Medium},
      'show-close-button': `boolean`,
      theme: {type: `string`, enum: Object.values(BannerTheme), default: BannerTheme.Info},
      tooltip: `json`,
      type: {type: `string`, enum: Object.values(BannerType), default: BannerType.Floating},
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {},
      helpers: {
        BannerAction,
        BannerSize,
        BannerType,
        getButtonType: (): ButtonType => BUTTON_TYPE_FROM_THEME.get(this.attr(`theme`) as BannerTheme),
        handleCloseButtonClick: (): void => {
          this.dispatchEvent(new CustomEvent(`action`, {detail: {actionName: BannerAction.Dismiss}}) as ActionEvent);
        },
        handleCtaButtonClick: (cta: CallToAction): void => {
          const {actionName, href, hrefTarget, download} = cta;

          if (actionName && actionName !== BannerAction.None) {
            this.dispatchEvent(new CustomEvent(`action`, {detail: {actionName}}) as ActionEvent);
          }

          this.trackBannerCtaClick(cta);

          // if user wishes to make a link button
          if (href) {
            if (download) {
              const tempAnchor = document.createElement(`a`);
              tempAnchor.href = href;
              tempAnchor.download = ``;
              tempAnchor.target = `_blank`;

              document.body.appendChild(tempAnchor);
              tempAnchor.click();
              document.body.removeChild(tempAnchor);
            } else {
              window.open(href, hrefTarget || `_blank`);
            }
          }
        },
      },
    };
  }

  public connectedCallback(): void {
    super.connectedCallback();
    requestAnimationFrame(() => {
      this.dispatchEvent(createBannerShownEventFor(this));
    });
  }

  private trackBannerCtaClick(cta: CallToAction): void {
    const {actionName, href, text, trackEventName, trackProps} = cta;

    if (trackEventName) {
      // Don't break legacy tracking for these banners
      this.dispatchEvent(
        new CustomEvent(`track`, {
          bubbles: true,
          composed: true,
          cancelable: true,
          detail: {
            eventName: trackEventName,
            props: trackProps || {
              'Action Name': actionName,
            },
          },
        }),
      );
    }

    // New tracking: "[mp-banner] Banner CTA Clicked" event
    this.dispatchEvent(
      new CustomEvent(`track`, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail: {
          eventName: TRACK_BANNER_CTA_CLICK,
          props: {
            'Action Name': actionName,
            'Banner Type': getTypePropOf(this),
            href,
            text,
            ...(trackProps || {}),
          },
        },
      }),
    );
  }
}

export const BannerEl = defineAndMakeJsxFactory<BannerJsxProps>(`mp-banner`, CtaBanner);
export const BannerReact = panel2react<BannerJsxProps>(`mp-banner`, CtaBanner);
